import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import camelCase from 'lodash/camelCase';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';

import Form from '../../../../../components/Form';
import Checkbox from '../../../../../components/Checkbox';
import { keysToObject } from '../../../../../utils/object';

import '../../styles.css';

/**
 * Returns selected groups with options and their checked state:
 * ```
 *  {
 *    Group A: {
 *      Option A1: true,
 *      Option A2: false,
 *    },
 *    Group B: {
 *      Option B1: false,
 *      Option B2: true,
 *    },
 *  }
 * ```
 * @param {string[]} values
 * @param {Object.<string, string[]>} allOptionsTree  all available options as array of option names, grouped by group name
 * @returns {Object.<string, Object.<string, boolean>>}
 */
const selectedOptionsFromValues = (values, allOptionsTree) => {
  // lookup map: option name => group name
  const optionsToGroupMap = Object.entries(allOptionsTree).reduce(
    (acc, [groupName, groupOptions]) => ({
      ...acc,
      ...keysToObject(groupOptions, groupName),
    }),
    {},
  );
  const value = values || /* istanbul ignore next */[];
  return value.reduce((acc, name) => {
    const group = optionsToGroupMap[name];
    acc[group] = acc[group] || keysToObject(allOptionsTree[group], false);
    acc[group][name] = true;
    return acc;
  }, {});
};

/**
 * Returns a flat list of selection options:
 * ```
 *   ['Option A1', 'Option B2']
 * ```
 * @param {Object.<string, string[]>} selectedOptionsTree
 */
const extractSelectedOptionNames = (selectedOptionsTree) =>
  Object.values(selectedOptionsTree).reduce((acc, groupOptions) => {
    Object.entries(groupOptions).forEach(([key, value]) => {
      /* istanbul ignore else */
      if (value) {
        acc.push(key);
      }
    });
    return acc;
  }, []);

const GroupedOptionsList = ({ values, options: allOptionsTree, onChange, renderEmptyGroup }) => {
  const [selectedOptions, setSelectedOptions] = useState(
    () => selectedOptionsFromValues(values, allOptionsTree),
  );

  const onGroupChange = useCallback((e, { value }) => {
    const prevGroups = Object.keys(selectedOptions);
    if (value.length < prevGroups.length) {
      const removed = difference(prevGroups, value);
      const nextSelectedOptions = omit(selectedOptions, removed);
      setSelectedOptions(nextSelectedOptions);
      onChange(extractSelectedOptionNames(nextSelectedOptions));
    } else {
      const added = difference(value, prevGroups)[0];
      const nextSelectedOptions = {
        ...selectedOptions,
        [added]: keysToObject(allOptionsTree[added], false),
      };
      setSelectedOptions(nextSelectedOptions);
      // no need to invoke onChange listener => adding a group does *not* affect selected options (yet)
    }
  }, [selectedOptions]);

  const onOptionChange = useCallback((option, group, checked) => {
    const nextSelectedOptions = {
      ...selectedOptions,
      [group]: {
        ...selectedOptions[group],
        [option]: checked,
      },
    };
    setSelectedOptions(nextSelectedOptions);
    onChange(extractSelectedOptionNames(nextSelectedOptions));
  }, [selectedOptions]);

  const selectedGroups = Object.keys(selectedOptions);
  const allGroups = useMemo(
    () =>
      sortBy(
        Object.keys(allOptionsTree).map((value) => ({
          value,
          text: value,
        })),
        'value',
      ),
    [values, allOptionsTree],
  );

  return (
    <div
      styleName="grouped-options-list-container"
      data-cy="Premium-Hem-GroupedOptionsList-Container"
    >
      <Form.Select
        fluid
        lazyLoad
        search
        selection
        multiple
        options={allGroups}
        value={selectedGroups}
        data-cy="Premium-Hem-GroupedOptionsList-Dropdown"
        onChange={onGroupChange}
      />
      {Object.entries(selectedOptions).map(([group, options]) => (
        <div
          key={camelCase(group)}
          styleName="grouped-options-list-item-container"
          data-cy="Premium-Hem-GroupedOptionsList-Group"
        >
          <p>{group}</p>
          {renderEmptyGroup && isEmpty(options) && renderEmptyGroup({ name: group })}
          {Object.entries(options).map(([option, checked]) => {
            const key = camelCase(option);
            const id = `checkbox_${key}`;
            return (
              <Form.Field key={key} data-cy="Premium-Hem-GroupedOptionsList-Field">
                <Checkbox
                  id={id}
                  label={{
                    htmlFor: id,
                    children: option,
                  }}
                  checked={!!checked}
                  onChange={() => onOptionChange(option, group, !checked)}
                />
              </Form.Field>
            );
          })}
        </div>
      ))}
    </div>
  );
};
GroupedOptionsList.propTypes = {
  onChange: PropTypes.func.isRequired,
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  options: PropTypes.object.isRequired,
  renderEmptyGroup: PropTypes.func,
};

export default GroupedOptionsList;
